import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Lock, KeyRound, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from "sonner";
import axios from 'axios';

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    // Supabase can send tokens in either query params or hash fragments
    // Try query params first
    let token = searchParams.get('access_token');
    
    // If not in query params, check hash fragment
    if (!token && location.hash) {
      const hashParams = new URLSearchParams(location.hash.substring(1));
      token = hashParams.get('access_token');
    }
    
    if (token) {
      setAccessToken(token);
    } else {
      setError('Invalid or expired reset link. Please request a new password reset.');
    }
  }, [searchParams, location.hash]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  const validatePassword = () => {
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }
    return true;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;

    setIsLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/supabase-auth/update-password`, {
        access_token: accessToken,
        new_password: formData.password
      });

      setIsSuccess(true);
      toast.success("Password updated successfully!");
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);

    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(error.response?.data?.detail || "Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen w-full grid lg:grid-cols-2 overflow-hidden">
      
      {/* Left Side - Form Area */}
      <div className="flex flex-col justify-center p-8 md:p-12 lg:p-16 bg-white relative h-full overflow-y-auto">
        <Link to="/login" className="absolute top-8 right-8 md:right-12 lg:right-16 inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Login
        </Link>

        <div className="max-w-md w-full mx-auto space-y-8 mt-12 lg:mt-0">
          <div className="text-center lg:text-left space-y-2">
            <Link to="/" className="inline-flex lg:hidden items-center gap-2 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
                alt="FaheemGPT Logo" 
                className="w-10 h-10 rounded-lg object-cover" 
              />
              <span className="text-xl font-bold text-slate-900">FaheemGPT</span>
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              {error ? 'Link Expired' : isSuccess ? 'Password Updated!' : 'Reset Password'}
            </h1>
            <p className="text-slate-500 text-lg">
              {error ? 'Please request a new reset link.' : isSuccess ? 'Your password has been successfully reset.' : 'Enter your new password below.'}
            </p>
          </div>

          {error ? (
            // Error State
            <div className="space-y-6 animate-in fade-in-50 slide-in-from-left-2 duration-300">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <p className="text-slate-600">{error}</p>
              </div>
              <Button 
                onClick={() => navigate('/login')} 
                className="w-full bg-blue-600 hover:bg-blue-700 h-11"
              >
                Back to Login
              </Button>
            </div>
          ) : isSuccess ? (
            // Success State
            <div className="space-y-6 animate-in fade-in-50 slide-in-from-left-2 duration-300">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-slate-600">Redirecting to login page...</p>
              </div>
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 animate-pulse" style={{ width: '100%' }}></div>
              </div>
            </div>
          ) : (
            // Reset Form
            <div className="space-y-6 animate-in fade-in-50 slide-in-from-left-2 duration-300">
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input 
                      id="password" 
                      value={formData.password} 
                      onChange={handleChange} 
                      required 
                      type="password" 
                      placeholder="Enter new password" 
                      className="pl-10 bg-slate-50" 
                      minLength={6}
                    />
                  </div>
                  <p className="text-xs text-slate-400">Must be at least 6 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-700">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                    <Input 
                      id="confirmPassword" 
                      value={formData.confirmPassword} 
                      onChange={handleChange} 
                      required 
                      type="password" 
                      placeholder="Confirm new password" 
                      className="pl-10 bg-slate-50" 
                    />
                  </div>
                </div>

                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 h-11" 
                  type="submit" 
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating Password...</>
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </form>
              
              <p className="text-center text-sm text-slate-500">
                Remember your password?{" "}
                <button onClick={() => navigate('/login')} className="text-slate-900 font-semibold hover:underline underline-offset-4">
                  Sign In
                </button>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Visual & Branding (Hidden on Mobile) */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 lg:p-16 bg-slate-900 text-white overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative z-10">
          <Link to="/" className="inline-flex items-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
              alt="FaheemGPT Logo" 
              className="w-10 h-10 rounded-lg object-cover border-2 border-white/10" 
            />
            <span className="text-xl font-bold tracking-tight">FaheemGPT</span>
          </Link>
        </div>

        <div className="relative z-10 max-w-lg space-y-8">
          <h2 className="text-4xl font-bold leading-tight">
            Secure your account with a strong password.
          </h2>
          
          <div className="space-y-6 pt-8">
            <div className="flex items-start gap-4 group">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                <CheckCircle2 className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-lg">Use at least 6 characters</h3>
                <p className="text-slate-400 text-sm leading-relaxed">Longer passwords are more secure.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 group">
              <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                <CheckCircle2 className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-lg">Mix letters and numbers</h3>
                <p className="text-slate-400 text-sm leading-relaxed">Combine different character types.</p>
              </div>
            </div>
            <div className="flex items-start gap-4 group">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-lg">Avoid common passwords</h3>
                <p className="text-slate-400 text-sm leading-relaxed">Choose something unique to you.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-4 text-sm text-slate-400 mt-auto">
          {null}
        </div>
      </div>

    </div>
  );
}
